import { useCallback, useEffect } from 'react';

import { useToastContext } from '@rtkwlf/fenrir-react';
import { QueryClientProvider } from '@tanstack/react-query';
import type {
  QueryCacheNotifyEvent,
  MutationCacheNotifyEvent,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { env } from '@packages/utils/env'; // TODO: Review this, where should env go?

import type { OnErrorToastData } from './query-client';
import { queryClient } from './query-client';

const getToastData = async (
  event: QueryCacheNotifyEvent | MutationCacheNotifyEvent,
  onErrorToastData: OnErrorToastData | undefined,
  error: Error | undefined,
) => {
  if (!onErrorToastData) return;

  const isUpdateError =
    event?.type === 'updated' && event?.action.type === 'error';

  if (!isUpdateError) return;

  if (typeof onErrorToastData === 'function') {
    return error ? await onErrorToastData(error) : undefined;
  }

  return onErrorToastData;
};

export const StateProvider = ({ children }: React.PropsWithChildren) => {
  const { addToast } = useToastContext();

  const onQueryCacheError = useCallback(
    async (event: QueryCacheNotifyEvent) => {
      const toastData = await getToastData(
        event,
        event.query.options.meta?.onErrorToastData,
        event.query.state.error,
      );

      if (toastData) {
        addToast(toastData);
      }
    },
    [addToast],
  );

  const onMutationCacheError = useCallback(
    async (event: MutationCacheNotifyEvent) => {
      const toastData = await getToastData(
        event,
        event.mutation?.options.meta?.onErrorToastData,
        event.mutation?.state.error,
      );

      if (toastData) {
        addToast(toastData);
      }
    },
    [addToast],
  );

  useEffect(() => {
    // queryClient is being used outside of React lifecycle
    // Subscribing to query and mutation cache errors to display error toasts
    // This ensures that errors occurring inside React lifecycle are handled
    const queryCacheUnsubscribe = queryClient
      .getQueryCache()
      .subscribe(onQueryCacheError);
    const mutationCacheUnsubscribe = queryClient
      .getMutationCache()
      .subscribe(onMutationCacheError);

    return () => {
      queryCacheUnsubscribe();
      mutationCacheUnsubscribe();
    };
  }, [onMutationCacheError, onQueryCacheError]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {env.MODE === 'development' && (
        <ReactQueryDevtools buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
};
